import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import siteConfig from '../config/site-config';
import { getGeolocationHttpsHint, isSecureGeolocationContext } from '../utils/secureContext';
import {
  classifyAccuracy,
  pickBestSample,
  normalizeSamples,
  normalizeCaptureThresholds,
  resolveAutoPoint,
  resolveManualPoint,
  hasMeaningfulManualPoint,
  isValidPickLngLat,
  PICK_SAMPLE_COLORS,
  PICK_SAMPLE_COUNT,
  PICK_SAMPLE_INTERVAL_MS
} from '../utils/pickPointCapture';

async function postPickPoint(path, body) {
  const response = await fetch(`${siteConfig.getApiUrl()}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body)
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok || data.success === false) {
    throw new Error(data.error || '请求失败');
  }
  return data;
}

/**
 * 手机选点：三次取样后等待确认提交；修改为可选，未改点不写手工坐标
 */
export default function usePickPointCapture({
  token,
  enabled,
  initialPoint,
  alreadyUsed,
  captureSettings
}) {
  const thresholds = useMemo(
    () => normalizeCaptureThresholds(captureSettings),
    [captureSettings]
  );
  const thresholdsRef = useRef(thresholds);
  thresholdsRef.current = thresholds;

  const [samples, setSamples] = useState(() => normalizeSamples(initialPoint?.capture_samples));
  const [latestFix, setLatestFix] = useState(null);
  const [autoPoint, setAutoPoint] = useState(() => resolveAutoPoint(initialPoint));
  const [manualPoint, setManualPoint] = useState(() => resolveManualPoint(initialPoint));
  const [editing, setEditing] = useState(false);
  const [awaitingConfirm, setAwaitingConfirm] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [locating, setLocating] = useState(!alreadyUsed);
  const [finalized, setFinalized] = useState(!!alreadyUsed && !!resolveAutoPoint(initialPoint));
  const [error, setError] = useState('');
  const [confirmStatus, setConfirmStatus] = useState(() => ({
    confirmed: !!initialPoint?.confirmed_at,
    message: initialPoint?.confirmed_message || null
  }));

  const samplesRef = useRef(samples);
  const latestFixRef = useRef(null);
  const recordingRef = useRef(false);
  const timerRef = useRef(null);
  const watchIdRef = useRef(null);
  const lastWaitingAtRef = useRef(0);
  const autoSubmitRef = useRef(false);

  samplesRef.current = samples;

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const reportWaiting = useCallback((fix) => {
    const now = Date.now();
    if (now - lastWaitingAtRef.current < 2000) return;
    lastWaitingAtRef.current = now;
    postPickPoint('/pick-point/progress', {
      token,
      event: 'waiting',
      lat: fix.lat,
      lng: fix.lng,
      accuracy: fix.accuracy
    }).catch(() => {});
  }, [token]);

  const completeSampling = useCallback((list) => {
    const best = pickBestSample(list);
    if (!best || !isValidPickLngLat(best.lng, best.lat)) return;
    clearTimer();
    recordingRef.current = false;
    setAutoPoint({ lat: best.lat, lng: best.lng, accuracy: best.accuracy });
    // 三次定位完成后自动提交，不再需要手动确认
    setAwaitingConfirm(false);
    setEditing(false);
    // 标记为正在提交，以便立即触发自动提交
    autoSubmitRef.current = true;
  }, [clearTimer]);

  const takeSample = useCallback((fix) => {
    const current = samplesRef.current;
    if (current.length >= PICK_SAMPLE_COUNT) return current;
    if (!isValidPickLngLat(fix.lng, fix.lat)) return current;
    const seq = current.length + 1;
    const sample = {
      seq,
      lat: fix.lat,
      lng: fix.lng,
      accuracy: fix.accuracy,
      color: PICK_SAMPLE_COLORS[seq - 1],
      captured_at: new Date().toISOString()
    };
    const next = [...current, sample];
    samplesRef.current = next;
    setSamples(next);
    postPickPoint('/pick-point/progress', {
      token,
      event: 'sample',
      seq,
      lat: sample.lat,
      lng: sample.lng,
      accuracy: sample.accuracy,
      color: sample.color
    }).catch((reportError) => {
      setError(reportError.message || '上报取样失败');
    });
    return next;
  }, [token]);

  const scheduleSample = useCallback(() => {
    clearTimer();
    timerRef.current = setTimeout(() => {
      const fix = latestFixRef.current;
      if (!fix || classifyAccuracy(fix.accuracy, thresholdsRef.current) !== 'ready') {
        scheduleSample();
        return;
      }
      const next = takeSample(fix);
      if (next.length >= PICK_SAMPLE_COUNT) {
        completeSampling(next);
        return;
      }
      scheduleSample();
    }, PICK_SAMPLE_INTERVAL_MS);
  }, [clearTimer, completeSampling, takeSample]);

  const handleFix = useCallback((position) => {
    const fix = {
      lat: position.coords.latitude,
      lng: position.coords.longitude,
      accuracy: position.coords.accuracy
    };
    if (!isValidPickLngLat(fix.lng, fix.lat)) return;
    latestFixRef.current = fix;
    setLatestFix(fix);
    setLocating(false);
    setError(''); // 清除之前的错误
    const grade = classifyAccuracy(fix.accuracy, thresholdsRef.current);
    reportWaiting(fix);
    if (grade !== 'ready') {
      return;
    }
    if (samplesRef.current.length >= PICK_SAMPLE_COUNT) {
      completeSampling(samplesRef.current);
      return;
    }
    if (!recordingRef.current) {
      recordingRef.current = true;
      scheduleSample();
    }
  }, [completeSampling, reportWaiting, scheduleSample]);

  const stopWatch = useCallback(() => {
    if (watchIdRef.current != null && navigator.geolocation) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }
  }, []);

  const startWatch = useCallback(() => {
    if (!isSecureGeolocationContext()) {
      const hint = getGeolocationHttpsHint();
      setError(hint || '定位功能需要 HTTPS 或 localhost 环境');
      setLocating(false);
      return;
    }
    if (!navigator.geolocation) {
      setError('当前浏览器不支持定位功能，请使用手机浏览器或更新浏览器版本');
      setLocating(false);
      return;
    }
    setError('');
    setLocating(true);
    stopWatch();
    watchIdRef.current = navigator.geolocation.watchPosition(
      handleFix,
      (locationError) => {
        setLocating(false);
        let errorMessage = '无法获取当前位置';
        switch (locationError.code) {
          case 1:
            errorMessage = '定位被拒绝。请在浏览器设置中允许位置权限，或点击下方手动选点';
            break;
          case 2:
            errorMessage = '定位不可用。请检查设备 GPS 是否开启，或点击下方手动选点';
            break;
          case 3:
            errorMessage = '定位超时。网络较慢或信号不佳，请稍后重试或点击下方手动选点';
            break;
          default:
            errorMessage = locationError.message || errorMessage;
        }
        setError(errorMessage);
      },
      { enableHighAccuracy: true, timeout: 25000, maximumAge: 0 }
    );
  }, [handleFix, stopWatch]);

  const retryLocate = useCallback(() => {
    samplesRef.current = [];
    setSamples([]);
    setAutoPoint(null);
    setManualPoint(null);
    setAwaitingConfirm(false);
    setEditing(false);
    recordingRef.current = false;
    startWatch();
  }, [startWatch]);

  useEffect(() => {
    const nextSamples = normalizeSamples(initialPoint?.capture_samples);
    if (nextSamples.length) {
      setSamples(nextSamples);
      samplesRef.current = nextSamples;
    }
    const nextAuto = resolveAutoPoint(initialPoint);
    if (nextAuto) setAutoPoint(nextAuto);
    const nextManual = resolveManualPoint(initialPoint);
    if (nextManual) setManualPoint(nextManual);
    if (alreadyUsed && nextAuto) {
      setFinalized(true);
      setAwaitingConfirm(false);
      recordingRef.current = false;
    }
  }, [alreadyUsed, initialPoint]);

  useEffect(() => {
    if (!enabled || alreadyUsed || finalized || awaitingConfirm) return undefined;
    const existing = normalizeSamples(initialPoint?.capture_samples);
    if (existing.length >= PICK_SAMPLE_COUNT) {
      completeSampling(existing);
    }
    return undefined;
  }, [alreadyUsed, awaitingConfirm, completeSampling, enabled, finalized, initialPoint]);

  useEffect(() => {
    if (!enabled || !token || alreadyUsed || finalized || awaitingConfirm) return undefined;
    startWatch();
    return () => {
      stopWatch();
      clearTimer();
      recordingRef.current = false;
    };
  }, [alreadyUsed, awaitingConfirm, clearTimer, enabled, finalized, startWatch, stopWatch, token]);

  useEffect(() => {
    if (finalized || awaitingConfirm) {
      stopWatch();
      clearTimer();
    }
  }, [awaitingConfirm, clearTimer, finalized, stopWatch]);

  const confirmSubmit = useCallback(async () => {
    if (confirming || finalized) return;
    const best = autoPoint || pickBestSample(samplesRef.current);
    if (!best || !isValidPickLngLat(best.lng, best.lat)) {
      setError('还没有可用的定位坐标');
      return;
    }
    const keepManual = hasMeaningfulManualPoint(best, manualPoint);
    setConfirming(true);
    try {
      await postPickPoint('/pick-point/submit', {
        token,
        lat: best.lat,
        lng: best.lng,
        accuracy: best.accuracy,
        samples: [best],
        auto_lat: best.lat,
        auto_lng: best.lng,
        auto_accuracy: best.accuracy
      });
      if (keepManual) {
        await postPickPoint('/pick-point/amend', {
          token,
          lat: manualPoint.lat,
          lng: manualPoint.lng
        });
      } else {
        setManualPoint(null);
      }
      samplesRef.current = [best];
      setSamples([{ ...best, seq: 1, color: PICK_SAMPLE_COLORS[0] }]);
      recordingRef.current = false;
      setAutoPoint({ lat: best.lat, lng: best.lng, accuracy: best.accuracy });
      setFinalized(true);
      setAwaitingConfirm(false);
      setEditing(false);
      setError('');
      autoSubmitRef.current = false;
    } catch (submitError) {
      setError(submitError.message || '确认提交失败');
      autoSubmitRef.current = false;
    } finally {
      setConfirming(false);
    }
  }, [autoPoint, confirming, finalized, manualPoint, token]);

  // 三次定位完成后自动提交
  useEffect(() => {
    if (autoSubmitRef.current && autoPoint && !finalized && !confirming) {
      confirmSubmit();
    }
  }, [autoPoint, confirming, confirmSubmit, finalized]);

  // 提交后轮询检查确认状态
  useEffect(() => {
    if (!finalized || !token || confirmStatus.confirmed) return undefined;
    
    const checkConfirmStatus = async () => {
      try {
        const response = await fetch(`${siteConfig.getApiUrl()}/pick-point/check?t=${encodeURIComponent(token)}`);
        const data = await response.json();
        if (data.success && data.token?.confirmed_at) {
          setConfirmStatus({
            confirmed: true,
            message: data.token.confirmed_message || '已成功上报信息，指挥部已确认。'
          });
        }
      } catch (checkError) {
        // 静默处理错误，继续轮询
        console.warn('检查确认状态失败:', checkError);
      }
    };
    
    // 立即检查一次
    checkConfirmStatus();
    
    // 每5秒轮询一次
    const intervalId = setInterval(checkConfirmStatus, 5000);
    
    return () => clearInterval(intervalId);
  }, [confirmStatus.confirmed, finalized, token]);

  const submitManual = useCallback(async (lng, lat) => {
    if (!isValidPickLngLat(lng, lat)) {
      setError('手工位置无效，请在地图上重新点选');
      return;
    }
    const next = { lat: Number(lat), lng: Number(lng) };
    setManualPoint(next);
    setEditing(false);
    setError('');
    if (!finalized) return;
    try {
      await postPickPoint('/pick-point/amend', { token, lat: next.lat, lng: next.lng });
    } catch (amendError) {
      setError(amendError.message || '提交手工位置失败');
    }
  }, [finalized, token]);

  const startEdit = useCallback(() => {
    if (!awaitingConfirm && !finalized) return;
    setEditing(true);
  }, [awaitingConfirm, finalized]);

  const accuracyClass = classifyAccuracy(latestFix?.accuracy, thresholds);
  const phase = useMemo(() => {
    if (editing) return 'editing';
    if (finalized && hasMeaningfulManualPoint(autoPoint, manualPoint)) return 'amended';
    if (finalized) return 'finalized';
    if (awaitingConfirm) return 'preview';
    if (!latestFix && locating) return 'locating';
    if (accuracyClass === 'too_large') return 'too_large';
    if (accuracyClass === 'waiting' || accuracyClass === 'unknown') {
      return latestFix ? 'waiting' : 'locating';
    }
    if (samples.length < PICK_SAMPLE_COUNT) return 'recording';
    return 'preview';
  }, [
    accuracyClass,
    autoPoint,
    awaitingConfirm,
    editing,
    finalized,
    latestFix,
    locating,
    manualPoint,
    samples.length
  ]);

  const recordingSeq = Math.min(PICK_SAMPLE_COUNT, samples.length + 1);

  return {
    phase,
    error,
    setError,
    locating,
    latestFix,
    samples,
    autoPoint,
    manualPoint,
    editing,
    awaitingConfirm,
    confirming,
    recordingSeq,
    thresholds,
    retryLocate,
    startEdit,
    submitManual,
    confirmSubmit,
    finalized,
    confirmStatus
  };
}
